import React from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import { Divider, Drawer } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EventIcon from "@mui/icons-material/Event";
import TuneIcon from "@mui/icons-material/Tune";
import PeopleIcon from "@mui/icons-material/People";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import PaymentIcon from "@mui/icons-material/Payment";

import AccountBoxIcon from "@mui/icons-material/AccountBox";
import SettingsIcon from "@mui/icons-material/Settings";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import NextWeekIcon from "@mui/icons-material/NextWeek";
// import AppleIcon from "@mui/icons-material/Apple";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
// import PieChartIcon from "@mui/icons-material/PieChart";
import DonutSmallIcon from "@mui/icons-material/DonutSmall";
import ExploreIcon from "@mui/icons-material/Explore";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import DevicesIcon from "@mui/icons-material/Devices";
import TabletIcon from "@mui/icons-material/Tablet";
import TabletMacIcon from "@mui/icons-material/TabletMac";
import ContactlessIcon from "@mui/icons-material/Contactless";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { FcSmartphoneTablet } from "react-icons/fc";
import PrintIcon from "@mui/icons-material/Print";
import WarningIcon from "@mui/icons-material/Warning";
import FoodBankIcon from "@mui/icons-material/FoodBank";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HistoryIcon from "@mui/icons-material/History";
import CampaignIcon from "@mui/icons-material/Campaign";
import { Profile, SidebarNav, Logout } from "./components";

import { useSelector } from "react-redux";
import {
  checkMyRestaurantPermission,
  checkMenuPermission,
  checkOrdersPermission,
  checkTransactionPermission,
  checkReportsPermission,
  checkBookingPermission,
  checkQueuePermission,
  checkStaffPermission,
  checkDishOrderReportPermission,
} from "util/userUtils";

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 240,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: 240,
      boxSizing: 'border-box',
      background: 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)',
      borderRight: 'none',
      zIndex: theme.zIndex.drawer,
      position: 'relative',
      [theme.breakpoints.up("lg")]: {
        marginTop: 80,
        height: "calc(100% - 80px)",
      },
      [theme.breakpoints.down("lg")]: {
        marginTop: 64,
        height: "calc(100% - 64px)",
      }
    },
  },
  root: {
    background: 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)',
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: theme.spacing(2),
    paddingTop: theme.spacing(8),
    overflow: 'auto',
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: '4px 0 20px rgba(0,0,0,0.08)',
    [theme.breakpoints.up('lg')]: {
      paddingTop: theme.spacing(6),
    }
  },
  divider: {
    margin: theme.spacing(2.5, 0),
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    height: '1px',
  },
  nav: {
    marginBottom: theme.spacing(2),
    flex: 1,
    padding: 0,
  },
}));

const Sidebar = (props) => {
  const { open, variant, onClose, className, ...rest } = props;

  const classes = useStyles();

  const user = useSelector((state) => state.account.user);

  let pages;

  const marketingSection = {
    title: "Marketing",
    href: "",
    icon: <ExploreIcon />,
    children: [
      {
        title: "Promotions",
        href: "/promotions",
        icon: <LocalOfferIcon />,
      },
      {
        title: "Vouchers",
        href: "/newVoucher",
        icon: <LocalActivityIcon />,
      },
    ],
  };

  const dashboardSection = {
    title: "Dashboard",
    href: "/dashboard",
    icon: <DashboardIcon />,
    children: [],
  };

  const myRestaurantSection = {
    title: "My Restaurant",
    href: "/myrestaurant",
    icon: <FastfoodIcon />,
    children: [],
  };

  const transactionSection = {
    title: "Transactions",
    href: "/transactions",
    icon: <PaymentIcon />,
    children: [],
  };

  const liveTransactionSection = {
    title: "Live Transactions",
    href: "/live-transactions",
    icon: <WatchLaterIcon />,
    children: [],
  };

  const orderSection = {
    title: "Orders",
    href: "/orders",
    icon: <ShoppingBasketIcon />,
    children: [],
  };

  const orderCallingSection = {
    title: "Order Calling",
    href: "/orderCalling",
    icon: <CampaignIcon />,
    children: [],
  };

  const riskSection = {
    title: "Risks",
    href: "/risks",
    icon: <WarningIcon />,
    children: [],
  };

  const menuSection = {
    title: "Menus",
    href: "/mymenus",
    icon: <RestaurantIcon />,
    children: [],
  };

  const bookingSection = {
    title: "Booking",
    href: "",
    icon: <AssignmentIcon />,
    children: [
      {
        title: "Booking Sessions",
        href: "/bookingSessions",
        icon: <LibraryBooksIcon />,
      },
      {
        title: "Bookings",
        href: "/bookings",
        icon: <ConfirmationNumberIcon />,
      },
      {
        title: "Queue",
        href: "/queueList",
        icon: <HourglassEmptyIcon />,
      },
    ],
  };

  // Managers see the queue only with the "queueManagement" permission, which is
  // separate from booking.
  const bookingSectionWithoutQueue = {
    ...bookingSection,
    children: bookingSection.children.filter((page) => page.href !== "/queueList"),
  };

  const queueSection = {
    title: "Queue",
    href: "/queueList",
    icon: <HourglassEmptyIcon />,
    children: [],
  };

  const accountSection = {
    title: "Account",
    href: "/account",
    icon: <AccountBoxIcon />,
    children: [],
  };

  const settingsSection = {
    title: "Settings",
    href: "/settings",
    icon: <SettingsIcon />,
    children: [],
  };

  const reportsSection = (user) => {
    const reportItems = [
      {
        title: "Sales Report",
        href: "/salesReport",
        icon: <ShowChartIcon />,
      },
      {
        title: "Payment Channels",
        href: "/paymentTypesReport",
        icon: <AccountBalanceWalletIcon />,
      },

      // {
      //   title: "Sales Details",
      //   href: "/salesDetailsReport",
      //   icon: <PieChartIcon />,
      // },

      {
        title: "Dish Report(Order)",
        href: "/dishesReport",
        icon: <EqualizerIcon />,
      },
      {
        title: "Dish Report(Transaction)",
        href: "/dishesTransactionReport",
        icon: <EqualizerIcon />,
      },
      {
        title: "Order Type",
        href: "/orderTypeReport",
        icon: <DonutSmallIcon />,
      },
      {
        title: "Pax Report",
        href: "/paxReport",
        icon: <PeopleAltIcon />,
      },
    ];

    if (!checkDishOrderReportPermission(user)) {
      reportItems.splice(2, 1);
    }

    const reports = {
      title: "Reports",
      href: "",
      icon: <AssessmentIcon />,
      children: reportItems,
    };
    return reports;
  };

  const staffSection = {
    title: "Staff",
    href: "",
    icon: <PeopleIcon />,
    children: [
      {
        title: "Staff List",
        href: "/staffList",
        icon: <PeopleIcon />,
      },
      {
        title: "Staff Activities",
        href: "/staffActivities",
        icon: <AssignmentIndIcon />,
      },
      {
        title: "Timesheets",
        href: "/timesheets",
        icon: <WatchLaterIcon />,
      },
    ],
  };

  // admin and menuAdmin only: settings shared by all merchants
  const generalSettingsSection = {
    title: "General Settings",
    href: "",
    icon: <TuneIcon />,
    children: [
      {
        // maintained public holidays (SA has no government API)
        title: "Public Holidays",
        href: "/generalSettings/publicHolidays",
        icon: <EventIcon />,
      },
    ],
  };

  const deviceSection =  {
    title: "Devices",
    href: "",
    icon: <DevicesIcon />,
    children: [
      {
        title: "Printers",
        href: "/merchants/printers",
        icon: <PrintIcon />,
      },
      {
        title: "POS Clients",
        href: "/posClients",
        icon: <FcSmartphoneTablet />,
      },
      {
        title: "Display Clients",
        href: "/displayClients",
        icon: <FcSmartphoneTablet />,
      },
      {
        title: "POS Devices",
        href: "/posDevices",
        icon: <TabletIcon />,
      },
      {
        title: "Kiosks",
        href: "/kioskDevices",
        icon: <TabletMacIcon />,
      },
    ],
  };

  const supportSection = {
    title: "Support",
    href: "",
    icon: <SupportAgentIcon />,
    children: [
      {
        title: "Support Portal",
        href: "/support",
        icon: <SupportAgentIcon />,
      },
      {
        title: "Support Admin",
        href: "/support/admin",
        icon: <AdminPanelSettingsIcon />,
      },
    ],
  };

  const supportStaffSection = {
    title: "Support",
    href: "/support",
    icon: <SupportAgentIcon />,
    children: [],
  };

  const foodcourtSection = {
    title: "Foodcourt",
    href: "/foodcourts",
    icon: <FoodBankIcon />,
    children: [],
  };

  //console.log('(user.email', user && user.email);
  const groupAdminMarketAndReportSections =
    user && user.email === "steven.fu@aupremium.com.au"
      ? [marketingSection, reportsSection(user)]
      : [reportsSection(user)];

  if (user) {
    if (user.role && user.role === "admin") {
      pages = [
        {
          title: "Admin",
          href: "",
          icon: <NextWeekIcon />,
          children: [
            {
              title: "Dashboard",
              href: "/adminDashboard",
              icon: <DashboardIcon />,
            },
            // {
            //   title: "Device useage",
            //   href: "/deviceUsage",
            //   icon: <AppleIcon />,
            // },
          ],
        },
        {
          title: "Dashboard",
          href: "/dashboard",
          icon: <DashboardIcon />,
          children: [],
        },
        {
          title: "Users",
          href: "/users",
          icon: <PeopleIcon />,
          children: [],
        },
        {
          title: "Customers",
          href: "/customers",
          icon: <PeopleAltIcon />,
          children: [],
        },
        {
          title: "Restaurants",
          href: "/restaurants",
          icon: <FastfoodIcon />,
          children: [],
        },
        deviceSection,
        generalSettingsSection,
        {
          title: "Adyen Payments",
          href: "",
          icon: <AccountBalanceIcon />,
          children: [
            {
              title: "Adyen Terminals",
              href: "/terminalDevices",
              icon: <ContactlessIcon />,
            },
            {
              title: "Reports",
              href: "/adyenReports",
              icon: <FileCopyIcon />,
            },
            {
              title: "Payment Profits",
              href: "/adyenPaymentProfits",
              icon: <MonetizationOnIcon />,
            },
          ],
        },
        {
          title: "My Restaurant",
          href: "/myrestaurant",
          icon: <FastfoodIcon />,
          children: [],
        },
        {
          title: "Subscriptions",
          href: "/subscriptionList",
          icon: <MonetizationOnIcon />,
          children: [],
        },
        {
          title: "Transactions",
          href: "/transactions",
          icon: <PaymentIcon />,
          children: [],
        },
        {
          title: "Live Transactions",
          href: "/live-transactions",
          icon: <WatchLaterIcon />,
          children: [],
        },
        {
          title: "Orders",
          href: "/orders",
          icon: <ShoppingBasketIcon />,
          children: [],
        },
        orderCallingSection,
      riskSection,
        {
          title: "Menus",
          href: "/mymenus",
          icon: <RestaurantIcon />,
          children: [],
        },

        {
          title: "Booking",
          href: "",
          icon: <AssignmentIcon />,
          children: [
            {
              title: "Booking Sessions",
              href: "/bookingSessions",
              icon: <LibraryBooksIcon />,
            },
            {
              title: "Bookings",
              href: "/bookings",
              icon: <ConfirmationNumberIcon />,
            },
            {
              title: "Queue",
              href: "/queueList",
              icon: <HourglassEmptyIcon />,
            },
          ],
        },
        staffSection,
        marketingSection,

        reportsSection(user),
        supportSection,
        foodcourtSection,
        {
          title: "Account",
          href: "/account",
          icon: <AccountBoxIcon />,
          children: [],
        },
        {
          title: "Settings",
          href: "/settings",
          icon: <SettingsIcon />,
          children: [],
        },
      ];
    } else if (user.role && (user.role === "support" || user.role === "supportAdmin")) {
      pages = [
        supportStaffSection,
        accountSection,
        settingsSection,
      ];
    } else if (user.role && user.role === "menuAdmin") {
      pages = [
        
       
        {
          title: "Restaurants",
          href: "/restaurants",
          icon: <FastfoodIcon />,
          children: [],
        },
        deviceSection,
        generalSettingsSection,
        {
          title: "Live Transactions",
          href: "/live-transactions",
          icon: <WatchLaterIcon />,
          children: [],
        },
      
      
        {
          title: "My Restaurant",
          href: "/myrestaurant",
          icon: <FastfoodIcon />,
          children: [],
        },
       
       
        {
          title: "Menus",
          href: "/mymenus",
          icon: <RestaurantIcon />,
          children: [],
        },
      orderSection,
        orderCallingSection,
        transactionSection,
        supportStaffSection,
        {
          title: "Settings",
          href: "/settings",
          icon: <SettingsIcon />,
          children: [],
        },
      ];
    }  else if (user.role && user.role === "restaurantGroupAdmin") {
      pages = [
        // {
        //   title: 'Admin',
        //   href: '',
        //   icon: <NextWeekIcon />,
        //   children: [
        //     {
        //       title: 'Dashboard',
        //       href: '/adminDashboard',
        //       icon: <DashboardIcon />
        //     },
        //     {
        //       title: 'Device useage',
        //       href: '/deviceUsage',
        //       icon: <AppleIcon />
        //     }
        //   ]
        // },
        {
          title: "Dashboard",
          href: "/dashboard",
          icon: <DashboardIcon />,
          children: [],
        },

        {
          title: "Customers",
          href: "/customers",
          icon: <PeopleAltIcon />,
          children: [],
        },
        {
          title: "Restaurants",
          href: "/restaurants",
          icon: <FastfoodIcon />,
          children: [],
        },
        {
          title: "Transactions",
          href: "/transactions",
          icon: <PaymentIcon />,
          children: [],
        },
        {
          title: "Live Transactions",
          href: "/live-transactions",
          icon: <WatchLaterIcon />,
          children: [],
        },
        {
          title: "Orders",
          href: "/orders",
          icon: <ShoppingBasketIcon />,
          children: [],
        },
        orderCallingSection,
       
        {
          title: "Menus",
          href: "/mymenus",
          icon: <RestaurantIcon />,
          children: [],
        },

        {
          title: "Booking",
          href: "",
          icon: <AssignmentIcon />,
          children: [
            {
              title: "Booking Sessions",
              href: "/bookingSessions",
              icon: <LibraryBooksIcon />,
            },
            {
              title: "Bookings",
              href: "/bookings",
              icon: <ConfirmationNumberIcon />,
            },
            {
              title: "Queue",
              href: "/queueList",
              icon: <HourglassEmptyIcon />,
            },
          ],
        },
        staffSection,
        ...groupAdminMarketAndReportSections,
        foodcourtSection,
        {
          title: "Account",
          href: "/account",
          icon: <AccountBoxIcon />,
          children: [],
        },
        {
          title: "Settings",
          href: "/settings",
          icon: <SettingsIcon />,
          children: [],
        },
      ];
    } else if (user.role && user.role === "restaurantAdmin") {
      pages = [
        dashboardSection,

        {
          title: "Customers",
          href: "/customers",
          icon: <PeopleAltIcon />,
          children: [],
        },
        myRestaurantSection,
        transactionSection,
        liveTransactionSection,
        orderSection,
        orderCallingSection,
        menuSection,

        bookingSection,
        staffSection,
        marketingSection,
        reportsSection(user),
        accountSection,
        settingsSection,
      ];
    } else if (user.role && user.role === "restaurantReport") {
      pages = [dashboardSection, reportsSection(user)];
    } else if (user.role && user.role === "restaurantManager") {
      if (!user.permissions || user.permissions.length === 0) {
        pages = [accountSection, settingsSection];
      } else {
        pages = [accountSection, settingsSection];

        if (checkOrdersPermission(user)) {
          pages.unshift(orderCallingSection);
          pages.unshift(orderSection);
        }

        if (checkTransactionPermission(user)) {
          pages.unshift(transactionSection);
          pages.unshift(liveTransactionSection);
        }

        if (checkMenuPermission(user)) {
          pages.unshift(menuSection);
        }

        if (checkReportsPermission(user)) {
          pages.unshift(reportsSection(user));
        }

        if (checkBookingPermission(user)) {
          pages.unshift(checkQueuePermission(user) ? bookingSection : bookingSectionWithoutQueue);
        } else if (checkQueuePermission(user)) {
          pages.unshift(queueSection);
        }
        if (checkStaffPermission(user)) {
          pages.unshift(staffSection);
        }

        if (checkMyRestaurantPermission(user)) {
          pages.unshift(myRestaurantSection);
        }
      }
    } else {
      pages = [];
    }
  } else {
    pages = [];
  }

  return (
    <Drawer
      anchor="left"
      classes={{ paper: classes.drawer }}
      onClose={onClose}
      open={open}
      variant={variant}
    >
      <div {...rest} className={clsx(classes.root, className)}>
        <Profile />
        <Divider className={classes.divider} />
        <SidebarNav className={classes.nav} pages={pages} />
        <Logout />
      </div>
    </Drawer>
  );
};

Sidebar.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired,
};

export default Sidebar;
